import React, { useContext, useMemo } from 'react';
import { useI18n } from '../../../../i18n';
import { TransactionStatus, DeliveryType } from '../../../../../typings/transaction';
import InfoIcon from '../../../../commons/components/icons/Info';
import { TAB_TYPE_BUYING, TAB_TYPE_SELLING, Tab } from '../../constants';
import TabsContext from '../contexts/TabsContext';
import useGetOrderStatusI18n from '../../hooks/useGetOrderStatusI18n';
import { ORDER_STATUS } from '../../../../commons/utils/constants';

import S from './style/AdBlock';

interface StatusInfoProps {
  status: TransactionStatus | '';
  packageDeliveryInfoUrl: string;
  packageDeliveryBuyerManualUrl: string;
  packageDeliverySellerManualUrl: string;
  showOrderSafeDealPackageDeliveredDescr: boolean;
  isPostingMethodExternalApp: boolean;
  goToReturns?: () => void;
  deliveryType?: DeliveryType;
  statusChangeTime?: string;
}

const StatusInfo: React.FC<StatusInfoProps> = ({
  status,
  packageDeliveryInfoUrl,
  packageDeliveryBuyerManualUrl,
  packageDeliverySellerManualUrl,
  showOrderSafeDealPackageDeliveredDescr,
  isPostingMethodExternalApp,
  goToReturns,
  deliveryType = 'P2P',
  statusChangeTime,
}) => {
  const i18n = useI18n();
  const tabType = useContext(TabsContext);
  const getParams = (time: string) => {
    if (time) {
      const date = new Date(time);
      date.setDate(date.getDate() + 1);
      const monthNum = date.getMonth().toString();
      const dayNum = date.getDate().toString();
      const currentTime = time?.split('T')[1].slice(0, 5);

      return {
        date: `${i18n(`dlv_returns_month_${monthNum}`)} ${dayNum}`,
        time: currentTime,
      };
    }

    return {};
  };
  const getStatusI18n = useGetOrderStatusI18n();
  const i18nStatusInfo = useMemo(
    () =>
      getStatusI18n({
        tabType,
        orderStatus: status,
        isInfo: true,
        deliveryType,
        params: getParams(statusChangeTime),
      }),
    [tabType, status, deliveryType, statusChangeTime],
  );

  const getTabType = (type: Tab) => (type === TAB_TYPE_BUYING ? 'buyer' : 'seller');

  const showStatusInfo =
    !showOrderSafeDealPackageDeliveredDescr && !isPostingMethodExternalApp && status;
  const showStatusLoading =
    !showOrderSafeDealPackageDeliveredDescr && !status && !isPostingMethodExternalApp;
  const showStatusIcon =
    i18nStatusInfo || isPostingMethodExternalApp || showOrderSafeDealPackageDeliveredDescr;
  const handleStatusInfoForReturns = () => {
    if (status === ORDER_STATUS.SAFEDEAL_REJECTED_BY_BUYER && tabType === TAB_TYPE_SELLING) {
      return i18n`dlv_status_info_seller_safedeal_rejected_by_buyer`;
    }
    if (status === ORDER_STATUS.SAFEDEAL_REJECTED_BY_BUYER && tabType === TAB_TYPE_BUYING) {
      return i18n`dlv_status_info_buyer_safedeal_rejected_by_buyer`;
    }
    return i18nStatusInfo;
  };

  return (
    <S.StatusInfo data-testid="status-info" data-cy="status-info">
      {showStatusIcon ? (
        <S.StatusInfoIcon data-testid="status-info-icon">
          <InfoIcon />
        </S.StatusInfoIcon>
      ) : null}
      <S.StatusInfoText data-testid="status-info-text">
        {showStatusInfo ? handleStatusInfoForReturns() : null}
        {showStatusLoading ? (
          <S.StatusLoading data-testid="status-info-loading">{i18nStatusInfo}</S.StatusLoading>
        ) : null}
        {status === ORDER_STATUS.SHIPMENT_LABEL_RECEIVED && tabType === TAB_TYPE_BUYING ? (
          <S.AdditionalLink href={packageDeliveryInfoUrl}>
            {i18n`dlv_status_info_buyer_shipment_label_received_link`}
          </S.AdditionalLink>
        ) : null}
        {status === ORDER_STATUS.REFUND_SENT && tabType === TAB_TYPE_BUYING ? (
          <S.AdditionalLink href={packageDeliveryBuyerManualUrl}>
            {i18n`dlv_status_info_buyer_refund_sent_link`}
          </S.AdditionalLink>
        ) : null}
        {status === ORDER_STATUS.REFUND_SENT && tabType === TAB_TYPE_SELLING ? (
          <S.AdditionalLink href={packageDeliverySellerManualUrl}>
            {i18n`dlv_status_info_seller_refund_sent_link`}
          </S.AdditionalLink>
        ) : null}
        {goToReturns && (
          <S.AdditionalLink onClick={goToReturns} data-cy="status-info-returns">
            {' '}
            {i18n`dlv_status_info_safedeal_cs_return_initiated_important`}
          </S.AdditionalLink>
        )}
        {showOrderSafeDealPackageDeliveredDescr
          ? i18n(`dlv_status_package_delivered_${getTabType(tabType)}_safedeal`)
          : null}
        {isPostingMethodExternalApp ? i18n`dlv_status_seller_shipment_status` : null}
      </S.StatusInfoText>
    </S.StatusInfo>
  );
};

export default StatusInfo;
